import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import styles from "./PanelList.module.css";
import { styled } from "@mui/material/styles";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Loader from "../../../Loader";
import Pagination from "../../../../ReusuableComponent/Pagination/Pagination";
import DatePickerSample from "../../../DatePicker";
import { AxiosInstance } from "../../../../../axios/index";

const ListPanelInfo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [updateListData, setUpdateListData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const txtBSearch = useRef(null);
  const txtFromDate = useRef(null);
  const txtToDate = useRef(null);
  const [sortOrder, setSortOrder] = useState("asc");
  const [categoryList, setCategoryList] = useState([]);
  const [courseList, setCourseList] = useState([]);
  const userJSON = sessionStorage.getItem("userDetails");
  const user = userJSON ? JSON.parse(userJSON) : null;
  const userId = user?.user_id;
  // Combined filter state
  const [filters, setFilters] = useState({
    searchTerm: "",
    category: [],
    course: [],
    dateRange: {
      from_date: "",
      to_date: "",
    },
    resultStatus: [],
  });

  // Applied filters for chip display
  const [appliedFilters, setAppliedFilters] = useState({
    searchTerm: "",
    category: [],
    course: [],
    dateRange: {
      from_date: "",
      to_date: "",
    },
    resultStatus: [],
  });

  // Temporary filter states (for editing before applying)
  const [tempFilters, setTempFilters] = useState({
    category: [],
    course: [],
    dateRange: {
      from_date: "",
      to_date: "",
    },
    resultStatus: [],
  });

  // Filter popup states
  const [isOpen, setIsOpen] = useState({
    category: false,
    course: false,
    interview_date: false,
    result_status: false,
  });

  // Refs for filter popups
  const filterCategoryRef = useRef(null);
  const filterCourseRef = useRef(null);
  const filterDateRef = useRef(null);
  const filterStatusRef = useRef(null);

  // Track if this is the initial mount
  const isInitialMount = useRef(true);
  // Track if we're restoring filters from location
  const isRestoringFromLocation = useRef(false);

  // Pagination calculations
  const start = (currentPage - 1) * rowsPerPage;
  const end = start + rowsPerPage;
  const currentData = updateListData?.slice(start, end);
  const totalPages = Math.ceil(updateListData?.length / rowsPerPage);

  // Separate the location.state restoration from the fetchPanelData trigger
  useEffect(() => {
    if (location.state) {
      const { FCAT, FCOURSE, FSTATUS, FSEARCH, FDATE } = location.state;

      // FCAT is array of category names
      // FCOURSE is array of course IDs
      const restoredCategory = Array.isArray(FCAT)
        ? FCAT.map((name) => ({ name, id: name }))
        : [];

      // IMPORTANT: Initially set course with IDs only, names will be populated after API call
      const restoredCourse = Array.isArray(FCOURSE)
        ? FCOURSE.map((id) => ({
            id: String(id),
            name: "Loading...", // Temporary placeholder
          }))
        : [];

      const restoredFilters = {
        searchTerm: FSEARCH ?? "",
        category: restoredCategory,
        course: restoredCourse,
        dateRange: FDATE ?? { from_date: "", to_date: "" },
        resultStatus: FSTATUS ?? [],
      };

      // Set the flag to indicate we're restoring from location
      isRestoringFromLocation.current = true;

      // Set all filter states
      setFilters(restoredFilters);
      setAppliedFilters(restoredFilters);
      setTempFilters(restoredFilters);

      // Clear the location state to prevent re-triggering on re-renders
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate]);

  const loadCategoryList = (apiData) => {
    const unique = [...new Set(apiData.map((item) => item.category))];

    const formatted = unique.map((name, index) => ({
      id: String(index + 1), // filter expects string IDs
      name,
    }));

    setCategoryList(formatted);
  };
  const loadCourseList = (apiData) => {
    const courseMap = new Map();

    apiData.forEach((item) => {
      if (item.course_name && item.category_course_id) {
        courseMap.set(item.category_course_id, {
          id: String(item.category_course_id),
          name: item.course_name,
          course_name: item.course_name,
        });
      }
    });

    setCourseList(Array.from(courseMap.values()));

    // Only update course names if we're not restoring from location
    // OR if we need to update the placeholder names
    if (isRestoringFromLocation.current) {
      setAppliedFilters((prev) => {
        const updatedCourse = prev.course.map((course) => {
          // Find the course name from the newly loaded course list
          const foundCourse = Array.from(courseMap.values()).find(
            (c) => c.id === course.id
          );
          return {
            ...course,
            name: foundCourse ? foundCourse.name : course.name,
          };
        });

        // Also update tempFilters to keep them in sync
        setTempFilters((prevTemp) => ({
          ...prevTemp,
          course: updatedCourse,
        }));

        return {
          ...prev,
          course: updatedCourse,
        };
      });

      // Also update the main filters state
      setFilters((prev) => {
        const updatedCourse = prev.course.map((course) => {
          const foundCourse = Array.from(courseMap.values()).find(
            (c) => c.id === course.id
          );
          return {
            ...course,
            name: foundCourse ? foundCourse.name : course.name,
          };
        });

        return {
          ...prev,
          course: updatedCourse,
        };
      });

      // Reset the flag after processing
      isRestoringFromLocation.current = false;
    }
  };

  // Fetch panel data based on applied filters
  // 1. Update the API payload in fetchPanelData function
  const fetchPanelData = async () => {
    setIsLoading(true);

    const apiPayload = {
      user_id: userId,
      category:
        appliedFilters.category.length > 0
          ? appliedFilters.category.map((c) => c.name)
          : "",
      course:
        appliedFilters.course.length > 0
          ? appliedFilters.course.map((c) => c.id)
          : "",
      start_date: appliedFilters.dateRange.from_date,
      end_date: appliedFilters.dateRange.to_date,
      status: appliedFilters.resultStatus.map((r) => r.id).join(","),
      search: appliedFilters.searchTerm,
    };

    try {
      const response = await AxiosInstance.post(
        "/api/mockinterview/panel/list",
        apiPayload
      );

      const apiData = response.data?.data || [];

      // Load dynamic lists
      loadCategoryList(apiData);
      loadCourseList(apiData); // This now handles the course name updates

      const withSerial = apiData.map((item, index) => ({
        ...item,
        serialNumber: index + 1,
      }));

      setUpdateListData(withSerial);
    } catch (err) {
      console.error("Error fetching panel list:", err);
      setUpdateListData([]);
    }

    setIsLoading(false);
  };

  // Trigger API call when appliedFilters change (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      // Fetch data on initial load
      fetchPanelData();
      return;
    }

    // Only fetch data if we're not currently restoring from location
    if (!isRestoringFromLocation.current) {
      // Reset to page 1 when filters change
      setCurrentPage(1);
      fetchPanelData();
    }
  }, [appliedFilters]);

  // Close filter popups when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        filterCategoryRef.current &&
        !filterCategoryRef.current.contains(event.target)
      ) {
        setIsOpen((prev) => ({ ...prev, category: false }));
      }
      if (
        filterCourseRef.current &&
        !filterCourseRef.current.contains(event.target)
      ) {
        setIsOpen((prev) => ({ ...prev, course: false }));
      }
      if (
        filterDateRef.current &&
        !filterDateRef.current.contains(event.target)
      ) {
        setIsOpen((prev) => ({ ...prev, interview_date: false }));
      }
      if (
        filterStatusRef.current &&
        !filterStatusRef.current.contains(event.target)
      ) {
        setIsOpen((prev) => ({ ...prev, result_status: false }));
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Search Handler
  const handleSearch = (event) => {
    if (event.key === "Enter") {
      const searchValue = event.target.value.trim();

      setAppliedFilters((prev) => ({
        ...prev,
        searchTerm: searchValue,
      }));
    }
  };

  const handleSearchFocus = () => {
    if (txtBSearch.current) {
      txtBSearch.current.focus();
      const event = new KeyboardEvent("keydown", {
        key: "Enter",
        keyCode: 13,
        which: 13,
        code: "Enter",
        bubbles: true,
      });
      txtBSearch.current.dispatchEvent(event);
    }
  };

  // Date Change Handler
  const handleDateChange = (dates, name) => {
    const dateSam = moment(dates).format("YYYY-MM-DD");
    setTempFilters((prevState) => ({
      ...prevState,
      dateRange: {
        ...prevState.dateRange,
        [name]: dateSam,
      },
    }));
  };

  // 4. Update the handleCheckboxChange for course to store ID and name
  const handleCheckboxChange = (event, typename) => {
    const { value, checked, name } = event.target;

    if (typename === "category") {
      setTempFilters((prev) => ({
        ...prev,
        category: checked
          ? [...prev.category, { id: value, name: name }]
          : prev.category.filter((item) => item.id !== value),
      }));
    } else if (typename === "course") {
      setTempFilters((prev) => ({
        ...prev,
        course: checked
          ? [
              ...prev.course,
              {
                id: value, // Store the course ID
                name: name, // Store the course name for display
              },
            ]
          : prev.course.filter((item) => item.id !== value),
      }));
    } else if (typename === "result_status") {
      setTempFilters((prev) => ({
        ...prev,
        resultStatus: checked
          ? [...prev.resultStatus, { id: value, name: name }]
          : prev.resultStatus.filter((item) => item.id !== value),
      }));
    }
  };

  // Filter Toggle Handler
  const handleFilter = (e, boxName) => {
    e.stopPropagation();

    if (boxName === "category") {
      setIsOpen({
        category: !isOpen.category,
        course: false,
        interview_date: false,
        result_status: false,
      });
      setTempFilters((prev) => ({
        ...prev,
        category: appliedFilters.category,
      }));
    } else if (boxName === "course") {
      setIsOpen({
        category: false,
        course: !isOpen.course,
        interview_date: false,
        result_status: false,
      });
      setTempFilters((prev) => ({
        ...prev,
        course: appliedFilters.course,
      }));
    } else if (boxName === "interview_date") {
      setIsOpen({
        category: false,
        course: false,
        interview_date: !isOpen.interview_date,
        result_status: false,
      });
      setTempFilters((prev) => ({
        ...prev,
        dateRange: appliedFilters.dateRange,
      }));
    } else if (boxName === "result_status") {
      setIsOpen({
        category: false,
        course: false,
        interview_date: false,
        result_status: !isOpen.result_status,
      });
      setTempFilters((prev) => ({
        ...prev,
        resultStatus: appliedFilters.resultStatus,
      }));
    }
  };

  // Apply Filter Handler
  const handleApplyFilter = (name) => {
    if (name === "category") {
      setAppliedFilters((prev) => ({
        ...prev,
        category: tempFilters.category,
      }));
      setIsOpen({ ...isOpen, category: false });
    } else if (name === "course") {
      setAppliedFilters((prev) => ({
        ...prev,
        course: tempFilters.course,
      }));
      setIsOpen({ ...isOpen, course: false });
    } else if (name === "interview_date") {
      setAppliedFilters((prev) => ({
        ...prev,
        dateRange: tempFilters.dateRange,
      }));
      setIsOpen({ ...isOpen, interview_date: false });
    } else if (name === "result_status") {
      setAppliedFilters((prev) => ({
        ...prev,
        resultStatus: tempFilters.resultStatus,
      }));
      setIsOpen({ ...isOpen, result_status: false });
    }
  };

  // Clear Filter Handler
  const handleClear = (name) => {
    if (name === "category") {
      setTempFilters((prev) => ({
        ...prev,
        category: [],
      }));
    } else if (name === "course") {
      setTempFilters((prev) => ({
        ...prev,
        course: [],
      }));
    } else if (name === "interview_date") {
      setTempFilters((prev) => ({
        ...prev,
        dateRange: {
          from_date: "",
          to_date: "",
        },
      }));
    } else if (name === "result_status") {
      setTempFilters((prev) => ({
        ...prev,
        resultStatus: [],
      }));
    }
  };

  // 6. Update chip deletion for course to use ID comparison
  const handleDeleteChip = (type, itemToDelete = null) => {
    if (type === "search") {
      setAppliedFilters((prev) => ({
        ...prev,
        searchTerm: "",
      }));
      if (txtBSearch.current) {
        txtBSearch.current.value = "";
      }
    } else if (type === "category") {
      setAppliedFilters((prev) => ({
        ...prev,
        category: prev.category.filter(
          (item) => item.name !== itemToDelete.name
        ),
      }));
    } else if (type === "course") {
      // Delete by course ID
      setAppliedFilters((prev) => ({
        ...prev,
        course: prev.course.filter((item) => item.id !== itemToDelete.id),
      }));
    } else if (type === "result_status") {
      setAppliedFilters((prev) => ({
        ...prev,
        resultStatus: prev.resultStatus.filter(
          (item) => item.id !== itemToDelete.id
        ),
      }));
    } else if (type === "interview_date") {
      setAppliedFilters((prev) => ({
        ...prev,
        dateRange: {
          from_date: "",
          to_date: "",
        },
      }));
    }
  };

  // Clear All Filters
  const handleAllClear = () => {
    const clearedFilters = {
      searchTerm: "",
      category: [],
      course: [],
      dateRange: {
        from_date: "",
        to_date: "",
      },
      resultStatus: [],
    };

    setAppliedFilters(clearedFilters);
    setTempFilters({
      category: [],
      course: [],
      dateRange: {
        from_date: "",
        to_date: "",
      },
      resultStatus: [],
    });

    if (txtBSearch.current) {
      txtBSearch.current.value = "";
    }
  };

  // Generate chip data for display
  const getChipData = () => {
    const chips = [];

    // Search chip
    if (appliedFilters.searchTerm) {
      chips.push({
        name: appliedFilters.searchTerm,
        type: "search",
      });
    }

    // Category chips
    appliedFilters.category.forEach((cat) => {
      chips.push({
        name: cat.name,
        type: "category",
        data: cat,
      });
    });

    // Course chips
    appliedFilters.course.forEach((course) => {
      chips.push({
        name: course.name,
        type: "course",
        data: course,
      });
    });

    // Date range chip
    if (
      appliedFilters.dateRange.from_date ||
      appliedFilters.dateRange.to_date
    ) {
      let dateLabel = "";
      if (
        appliedFilters.dateRange.from_date &&
        appliedFilters.dateRange.to_date
      ) {
        dateLabel = `${moment(appliedFilters.dateRange.from_date).format(
          "DD/MM/YYYY"
        )} - ${moment(appliedFilters.dateRange.to_date).format("DD/MM/YYYY")}`;
      } else if (appliedFilters.dateRange.from_date) {
        dateLabel = `From: ${moment(appliedFilters.dateRange.from_date).format(
          "DD/MM/YYYY"
        )}`;
      } else if (appliedFilters.dateRange.to_date) {
        dateLabel = `To: ${moment(appliedFilters.dateRange.to_date).format(
          "DD/MM/YYYY"
        )}`;
      }
      chips.push({
        name: dateLabel,
        type: "interview_date",
      });
    }

    // Result status chips
    appliedFilters.resultStatus.forEach((status) => {
      chips.push({
        name: status.name,
        type: "result_status",
        data: status,
      });
    });

    return chips;
  };

  // Sorting Handler
  const handleSort = (event, sortKey) => {
    event.preventDefault();
    let order = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(order);

    const sortedList = [...updateListData].sort((a, b) => {
      if (a[sortKey] < b[sortKey]) return order === "asc" ? -1 : 1;
      if (a[sortKey] > b[sortKey]) return order === "asc" ? 1 : -1;
      return 0;
    });

    setUpdateListData(sortedList);
  };

  // Pagination Handlers
  const handlePrev = (val) => {
    setCurrentPage(val);
  };

  const handleNext = (val) => {
    setCurrentPage(val);
  };

  const handlePageCount = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // 7. Update the navigation state to preserve the filter data structure
  const handleViewPanel = (id,markStatus) => {
    navigate(`/Viewpanel`, {
      state: {
        PANELID: id,
        // Send category names
        FCAT: appliedFilters.category.map((cat) => cat.name),
        // Send course IDs
        FCOURSE: appliedFilters.course.map((course) => course.id),
        FSTATUS: appliedFilters.resultStatus,
        FSEARCH: appliedFilters.searchTerm,
        FDATE: appliedFilters.dateRange,
        PAGE_FROM:"l",
        TYPE:"PanelList",
        PANEL_MARK_STATUS: markStatus,
      },
    });
  };
 
  

  // Styled ListItem for Chips
  const ListItem = styled("li")(({ theme }) => ({
    margin: theme.spacing(0.5),
  }));

  // Check if filters are active
  const isDateFilterActive =
    appliedFilters.dateRange.from_date || appliedFilters.dateRange.to_date;

  const chipData = getChipData();

  return (
    <div
      className={`${styles.CourseInfoSec} ${styles.cm_ListViewForm} container-fluid global_container p-0`}
    >
      <div className={`${styles.cm_container} BathHeader`}>
        {/* Header */}
        <div className="col-md-6 col-12">
          <h2 className="TitleHeader">Panel List</h2>
        </div>

        {/* Search Bar */}
        <div className="row">
          <div className="col-lg-4 col-md-4 col-sm-12 col-xs-12 mb-2">
            <div className="input-group">
              <input
                ref={txtBSearch}
                type="text"
                className={styles.Inputs}
                id="SearchInput"
                placeholder="Search by Panel Name and Interview Name"
                defaultValue={appliedFilters.searchTerm}
                onKeyDown={handleSearch}
              />
              <span
                className={styles.input_group_text}
                id="SearchInput"
                onClick={handleSearchFocus}
              >
                <span className="material-icons">search</span>
              </span>
            </div>
          </div>
        </div>

        {/* Filter Chips */}
        {chipData.length > 0 && (
          <div className="row mt-3">
            <div className="col-md-12 col-sm-12 col-xs-12 paperil_global">
              <Paper
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  flexWrap: "wrap",
                  listStyle: "none",
                  p: 0.5,
                  m: 0,
                }}
                component="ul"
              >
                {chipData.map((chip, index) => (
                  <ListItem key={index}>
                    <Chip
                      label={chip.name}
                      onDelete={() => handleDeleteChip(chip.type, chip.data)}
                    />
                  </ListItem>
                ))}
                <ListItem className="clearAll_list">
                  <a className="clearAll_listItem" onClick={handleAllClear}>
                    Clear All Filters
                  </a>
                </ListItem>
              </Paper>
            </div>
          </div>
        )}

        {/* Table Section */}
        <div className="row mx-0 mt-2">
          <div className="col-md-12 table-responsive tableMinHeight px-0">
            <table className="table" id="tableListHeader">
              <thead>
                <tr>
                  {/* S.No */}
                  <th id="th">
                    <span
                      className="textHead_th"
                      onClick={(e) => handleSort(e, "serialNumber")}
                    >
                      S.No
                    </span>
                    <span
                      className="textHead_th material-icons align-middle"
                      onClick={(e) => handleSort(e, "serialNumber")}
                    >
                      import_export
                    </span>
                  </th>

                  {/* Panel Name */}
                  <th id="th">
                    <span
                      className="textHead_th"
                      onClick={(e) => handleSort(e, "panel_name")}
                    >
                      Panel Name
                    </span>
                    <span
                      className="textHead_th material-icons align-middle"
                      onClick={(e) => handleSort(e, "panel_name")}
                    >
                      import_export
                    </span>
                  </th>

                  {/* Interview Name */}
                  <th id="th">
                    <span
                      className="textHead_th"
                      onClick={(e) => handleSort(e, "interview_name")}
                    >
                      Interview Name
                    </span>
                    <span
                      className="textHead_th material-icons align-middle"
                      onClick={(e) => handleSort(e, "interview_name")}
                    >
                      import_export
                    </span>
                  </th>

                  {/* Category */}
                  <th id="th" className={styles.tableThPadding}>
                    <span
                      className="textHead_th"
                      onClick={(e) => handleSort(e, "category")}
                    >
                      Category
                    </span>
                    <span
                      className="textHead_th material-icons align-middle"
                      onClick={(e) => handleSort(e, "category")}
                    >
                      import_export
                    </span>
                    <span
                      className={`textHead_th align-middle material-icons ${
                        appliedFilters.category.length > 0
                          ? styles.active_icons
                          : ""
                      } ${isOpen.category ? styles.active_icons : ""}`}
                      onClick={(e) => handleFilter(e, "category")}
                    >
                      filter_list
                    </span>

                    {isOpen.category && (
                      <div
                        className="filterBoxs_testlist mt-3"
                        ref={filterCategoryRef}
                      >
                        <div className="col-md-12 form-check filterStatus_list">
                          <ul className="ulFilterListData">
                            {categoryList.map((el) => (
                              <li className="filterListData" key={el.id}>
                                <input
                                  className="ListCheckbox"
                                  type="checkbox"
                                  name={el.name}
                                  value={el.id}
                                  id={`cat_${el.id}`}
                                  checked={tempFilters.category.some(
                                    (element) => element.id === String(el.id)
                                  )}
                                  onChange={(e) =>
                                    handleCheckboxChange(e, "category")
                                  }
                                />
                                <label
                                  className="form-check-label filterChipText_withoutBG"
                                  htmlFor={`cat_${el.id}`}
                                >
                                  {el.name}
                                </label>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="borderWidth row">
                          <ul className="List-Button">
                            <Link
                              to="#"
                              onClick={(e) => {
                                e.preventDefault();
                                handleClear("category");
                              }}
                              className="secondaryButton"
                            >
                              Clear
                            </Link>
                            <li className="ListBtnItems">
                              <Link
                                to="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleApplyFilter("category");
                                }}
                                className="primaryButton"
                              >
                                Apply Filter
                              </Link>
                            </li>
                          </ul>
                        </div>
                      </div>
                    )}
                  </th>

                  {/* Course */}
                  <th id="th" className={styles.tableThPadding}>
                    <span
                      className="textHead_th"
                      onClick={(e) => handleSort(e, "course")}
                    >
                      Course
                    </span>
                    <span
                      className="textHead_th material-icons align-middle"
                      onClick={(e) => handleSort(e, "course")}
                    >
                      import_export
                    </span>
                    <span
                      className={`textHead_th align-middle material-icons ${
                        appliedFilters.course.length > 0
                          ? styles.active_icons
                          : ""
                      } ${isOpen.course ? styles.active_icons : ""}`}
                      onClick={(e) => handleFilter(e, "course")}
                    >
                      filter_list
                    </span>

                    {isOpen.course && (
                      <div
                        className="filterBoxs_testlist mt-3"
                        ref={filterCourseRef}
                      >
                        <div className="col-md-12 form-check filterStatus_list">
                          <ul className="ulFilterListData">
                            {courseList.map((el) => (
                              <li className="filterListData" key={el.id}>
                                <input
                                  className="ListCheckbox"
                                  type="checkbox"
                                  name={el.name}
                                  value={el.id}
                                  id={`course_${el.id}`}
                                  checked={tempFilters.course.some(
                                    (element) => element.id === String(el.id)
                                  )}
                                  onChange={(e) =>
                                    handleCheckboxChange(e, "course")
                                  }
                                />
                                <label
                                  className="form-check-label filterChipText_withoutBG"
                                  htmlFor={`course_${el.id}`}
                                >
                                  {el.name}
                                </label>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="borderWidth row">
                          <ul className="List-Button">
                            <Link
                              to="#"
                              onClick={(e) => {
                                e.preventDefault();
                                handleClear("course");
                              }}
                              className="secondaryButton"
                            >
                              Clear
                            </Link>
                            <li className="ListBtnItems">
                              <Link
                                to="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleApplyFilter("course");
                                }}
                                className="primaryButton"
                              >
                                Apply Filter
                              </Link>
                            </li>
                          </ul>
                        </div>
                      </div>
                    )}
                  </th>

                  {/* Interview Date */}
                  <th id="th" className={styles.tableThPadding}>
                    <span
                      className="textHead_th"
                      onClick={(e) => handleSort(e, "interview_date")}
                    >
                      Interview Date
                    </span>
                    <span
                      className="textHead_th material-icons align-middle"
                      onClick={(e) => handleSort(e, "interview_date")}
                    >
                      import_export
                    </span>
                    <span
                      className={`textHead_th align-middle material-icons ${
                        isDateFilterActive ? styles.active_icons : ""
                      } ${isOpen.interview_date ? styles.active_icons : ""}`}
                      onClick={(e) => handleFilter(e, "interview_date")}
                    >
                      filter_list
                    </span>

                    {/* Date Filter Popup */}
                    {isOpen.interview_date && (
                      <div
                        className="filterBoxs_testlist mt-3"
                        ref={filterDateRef}
                        style={{ minWidth: "280px" }}
                      >
                        <div className="col-md-12 filterStatus_list">
                          <div className="row">
                            <div className="col-12 mb-2">
                              <label className="form-check-label filterChipText_withoutBG d-block mb-1">
                                From Date
                              </label>
                              <DatePickerSample
                                txtAdMDob={txtFromDate}
                                name={"from_date"}
                                handleChange={handleDateChange}
                                fieldInput={tempFilters.dateRange.from_date}
                                // maxDate={
                                //   tempFilters.dateRange.to_date
                                //     ? new Date(tempFilters.dateRange.to_date)
                                //     : null
                                // }
                              />
                            </div>
                            <div className="col-12 mb-2">
                              <label className="form-check-label filterChipText_withoutBG d-block mb-1">
                                To Date
                              </label>
                              <DatePickerSample
                                txtAdMDob={txtToDate}
                                name={"to_date"}
                                handleChange={handleDateChange}
                                fieldInput={tempFilters.dateRange.to_date}
                                minDate={
                                  tempFilters.dateRange.from_date
                                    ? new Date(tempFilters.dateRange.from_date)
                                    : null
                                }
                              />
                            </div>
                          </div>
                        </div>
                        <div className="borderWidth row">
                          <ul className="List-Button">
                            <li className="ListBtnItems">
                              <Link
                                to="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleClear("interview_date");
                                }}
                                className="secondaryButton ms-2"
                              >
                                Clear
                              </Link>
                            </li>
                            <li className="ListBtnItems">
                              <Link
                                to="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleApplyFilter("interview_date");
                                }}
                                className="primaryButton"
                              >
                                Apply Filter
                              </Link>
                            </li>
                          </ul>
                        </div>
                      </div>
                    )}
                  </th>

                  {/* Result Status */}
                  <th id="th" className={styles.tableThPadding}>
                    <span className="textHead_th">Result Status</span>
                    <span
                      className={`textHead_th align-middle material-icons ${
                        appliedFilters.resultStatus.length > 0
                          ? styles.active_icons
                          : ""
                      } ${isOpen.result_status ? styles.active_icons : ""}`}
                      onClick={(e) => handleFilter(e, "result_status")}
                    >
                      filter_list
                    </span>

                    {isOpen.result_status && (
                      <div
                        className="filterBoxs_testlist mt-3"
                        ref={filterStatusRef}
                      >
                        <div className="col-md-12 form-check filterStatus_list">
                          <ul className="ulFilterListData">
                            <li className="filterListData">
                              <input
                                className="ListCheckbox"
                                type="checkbox"
                                name="Not Yet Published"
                                value="0"
                                id="status_0"
                                checked={tempFilters.resultStatus.some(
                                  (element) => element.id === "0"
                                )}
                                onChange={(e) =>
                                  handleCheckboxChange(e, "result_status")
                                }
                              />
                              <label
                                className="form-check-label filterChipText"
                                htmlFor="status_0"
                                style={{
                                  backgroundColor: "#e91e63",
                                  color: "#fff",
                                }}
                              >
                                Not Yet Published
                              </label>
                            </li>
                            <li className="filterListData">
                              <input
                                className="ListCheckbox"
                                type="checkbox"
                                name="Published"
                                value="1"
                                id="status_1"
                                checked={tempFilters.resultStatus.some(
                                  (element) => element.id === "1"
                                )}
                                onChange={(e) =>
                                  handleCheckboxChange(e, "result_status")
                                }
                              />
                              <label
                                className="form-check-label filterChipText bg-green"
                                htmlFor="status_1"
                              >
                                Published
                              </label>
                            </li>
                          </ul>
                        </div>
                        <div className="borderWidth row">
                          <ul className="List-Button">
                            <li className="ListBtnItems">
                              <Link
                                to="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleClear("result_status");
                                }}
                                className="secondaryButton ms-2"
                              >
                                Clear
                              </Link>
                            </li>
                            <li className="ListBtnItems">
                              <Link
                                to="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleApplyFilter("result_status");
                                }}
                                className="primaryButton"
                              >
                                Apply Filter
                              </Link>
                            </li>
                          </ul>
                        </div>
                      </div>
                    )}
                  </th>

                  {/* Action */}
                  <th id="th">
                    <span className="textHead_th">Action</span>
                  </th>
                </tr>
              </thead>

              <tbody>
                {currentData.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: "center" }}>
                      No records to display
                    </td>
                  </tr>
                ) : (
                  currentData?.map((panel, index) => (
                    <tr key={panel.id || index}>
                      <td id="td">{panel.serialNumber}</td>
                      <td id="td">{panel.panel_name}</td>
                      <td id="td">{panel.exam_title}</td>
                      <td id="td">{panel.category}</td>
                      <td id="td">{panel.course_name}</td>
                      <td id="td">
                        {moment(panel.started_dt).format("DD/MM/YYYY")}
                      </td>
                      <td id="td">
                        {panel.result_status === 0 ? (
                          <span
                            className="filterChipText mx-0"
                            style={{
                              backgroundColor: "#e91e63",
                              color: "#fff",
                            }}
                          >
                            Not Yet Published
                          </span>
                        ) : (
                          <span className="filterChipText bg-green mx-0">
                            Published
                          </span>
                        )}
                      </td>
                      <td id="td">
                        <button
                          className="view_icon_button"
                          onClick={() => handleViewPanel(panel.id,panel.mark_status)}
                        >
                          <span className="visibleicon material-icons">
                            visibility
                          </span>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="row mt-2">
          <Pagination
            rowsPerPage={rowsPerPage}
            handlePageCount={handlePageCount}
            startPage={
              updateListData && updateListData?.length > 0
                ? currentPage * rowsPerPage + 1 - rowsPerPage
                : 0
            }
            endPage={
              updateListData && updateListData?.length > 0
                ? updateListData?.length < currentPage * rowsPerPage
                  ? updateListData?.length
                  : currentPage * rowsPerPage
                : 0
            }
            totalRecords={
              updateListData && updateListData?.length > 0
                ? updateListData?.length
                : 0
            }
            handlePrev={() => handlePrev(currentPage - 1)}
            currentPrevPage={currentPage === 1}
            prevButton={`${styles.prev_buttonViewEvaluator} ${styles.materialIconsRightAndLeft} me-2`}
            handleNext={() => handleNext(currentPage + 1)}
            currentNextPage={currentPage === totalPages}
          />
        </div>
      </div>

      {/* Loader */}
      {isLoading && (
        <div
          className="loaderApi"
          style={{
            display: isLoading ? "flex" : "none",
            justifyContent: "center",
            alignItems: "center",
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "white",
            zIndex: 999,
          }}
        >
          <Loader />
        </div>
      )}
    </div>
  );
};

export default ListPanelInfo;
